import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { get, isUnauthorized, post, type LoginResult, type Me } from './api';
import { queryClient } from '../features/queryClient';

/**
 * What the bootstrap learned about the session.
 *
 * `signed-out` is a FACT: the server said 401. `unavailable` is an ADMISSION:
 * the request failed for a reason that says nothing about the session — a 500,
 * a proxy hiccup, a phone going through a tunnel. The two used to be collapsed
 * into `me === null`, so a transient API failure showed an authenticated
 * administrator the sign-in screen. They had not been signed out; they were
 * told they had, which is worse than an error message because it is untrue.
 */
export type SessionStatus = 'signed-in' | 'signed-out' | 'unavailable';

interface AuthState {
  me: Me | null;
  loading: boolean;
  status: SessionStatus;
  /** Re-run the `/auth/me` bootstrap — the retry behind the unavailable state. */
  retry: () => void;
  login: (subject: string) => Promise<void>;
  switchOrg: (orgId: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/** dev-login may return { needsOrgSelection } when the identity has >1 active
 *  org. In that case no session is minted yet; the caller renders the chooser. */
interface DevLoginResult extends LoginResult {
  needsOrgSelection?: boolean;
}

/**
 * DEVELOPMENT ONLY. Exchange an `external_auth_subject` for a session.
 *
 * WHY THE GUARD IS HERE AND NOT ONLY AT THE CALL SITE.
 *
 * `POST /auth/dev-login` is hard-gated server-side: it answers 404 unless
 * `AUTH_TEST_MODE === true` and the tier is not production, and `AUTH_TEST_MODE`
 * itself refuses to boot outside `APP_ENV=development`. So this was never
 * exploitable, and it is not being fixed because it was.
 *
 * It is being fixed because the client's half of that intent lived entirely in
 * one JSX conditional — `{import.meta.env.DEV ? <DevLoginForm … /> : null}` in
 * `features/signin/SignIn.tsx`. The function it guards said nothing about being
 * development-only, so a second caller, a refactor that lifts the form, or a
 * component extracted into a shared shell would carry a production sign-in path
 * to `/auth/dev-login` and nothing in this app would notice. The server would
 * still refuse it — and the failure would arrive as an unexplained 404 on a
 * sign-in screen, which is a bad way to learn that a guard was dropped.
 *
 * Stating it on the function makes the intent travel WITH the capability rather
 * than beside it, and `import.meta.env.DEV` is a build-time constant that Vite
 * substitutes, so in a production bundle this is an unconditional throw and the
 * `post` below is unreachable code rather than a live route.
 *
 * The worker app reached the same conclusion with React Native's equivalent
 * flag — see `apps/worker-mobile/src/lib/session-request.ts`'s
 * `requestSessionToken` and the `__DEV__` gate in `app/login.tsx`.
 */
export async function devLoginRequest(subject: string): Promise<DevLoginResult> {
  if (!import.meta.env.DEV) {
    throw new Error(
      'Development-only sign-in is not available in this build. Use your organization’s identity provider or your password.',
    );
  }
  return post<DevLoginResult>('/auth/dev-login', { subject });
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [status, setStatus] = useState<SessionStatus>('signed-out');
  const [loading, setLoading] = useState(true);
  /** Bumped by `retry()`; re-runs the bootstrap effect. */
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    get<Me>('/auth/me')
      .then((m) => {
        if (cancelled) return;
        setMe(m);
        setStatus('signed-in');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setMe(null);
        // Only the server saying "unauthorized" is evidence that there is no
        // session. Anything else leaves the question open, and the app says so.
        setStatus(isUnauthorized(err) ? 'signed-out' : 'unavailable');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  const login = useCallback(async (subject: string) => {
    const result = await devLoginRequest(subject);
    if (result.needsOrgSelection) {
      // No session yet; the chooser will call selectOrg. Leave `me` null so the
      // app renders the chooser (rather than a signed-in shell with no org).
      setMe(null);
      setStatus('signed-out');
      return;
    }
    const m = await get<Me>('/auth/me');
    setMe(m);
    setStatus('signed-in');
  }, []);

  const switchOrg = useCallback(async (orgId: string) => {
    await post('/auth/switch-org', { orgId });
    // EVERY cached read belongs to the organization it was fetched under.
    // Programs, sites, people, schedules, dashboards — none of it is valid for
    // the new tenant, and a stale entry would show the previous organization's
    // records under the new one's name. Clearing (not invalidating) also drops
    // in-flight results, so a request that started before the switch cannot
    // land afterwards. The server enforces the boundary regardless; this stops
    // the browser from displaying something it should never have kept.
    queryClient.clear();
    const m = await get<Me>('/auth/me');
    setMe(m);
    setStatus('signed-in');
  }, []);

  const logout = useCallback(async () => {
    try {
      await post('/auth/logout', {});
    } finally {
      // Same reasoning as switchOrg: nothing cached survives the session.
      queryClient.clear();
      setMe(null);
      setStatus('signed-out');
    }
  }, []);

  return (
    <AuthContext.Provider value={{ me, loading, status, retry, login, switchOrg, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
